// The agent round loop, extracted so all three callers share ONE implementation.
//
// WHY THIS MODULE EXISTS. The loop lived inside `ChatPanel.runChatRound` as a closure
// over component state, so it could not be reused and could not be tested (ChatPanel
// pulls the SSE client and renders). The two background callers in `ProjectPage`
// (`runBackgroundWorkflowTurn`, `runBackgroundTileTurn`) therefore each had their own
// hand-rolled copy that ran exactly ONE round and threw the `awaiting_tool_results`
// event away. That is why the workflow agent could emit five `create_tile` calls and
// then stop: the new node ids were never posted back, so it never got the turn in which
// it would call `connect_tiles` and `activate_tile`.
//
// THE PROTOCOL. The backend is stateless per round and the client owns the loop:
//   1. stream a round; collect `text` and `tool_call` events
//   2. on `awaiting_tool_results`, execute the calls and append
//      [assistant tool_use blocks, user tool_result blocks] to the history
//   3. repeat until a round emits no tool calls (the terminator) or the cap is hit
// BOTH backend generators must emit `awaiting_tool_results`, or step 2 never fires and
// the loop dead-ends — see product_backend/tests/test_chat_round_loop.py.
//
// This module is PURE with respect to React: every dependency is injected, so the unit
// tests drive it with a fake stream and a fake executor.

// A pipeline build is create_tile -> connect_tiles -> activate_tile, and a subagent may
// read state, act, then complete — so the old cap of 10 was never the binding limit
// (the missing event was). 16 leaves room for a wide graph plus narration while still
// terminating a model that has begun looping on itself.
export const MAX_ROUNDS = 16;

// --- Tool-call summaries -----------------------------------------------------
//
// CHANGE ONE, CHANGE BOTH: `product_backend/app/services/agent_runner/loop.py`
// `_summarize_tool_calls` is the mirror of everything down to `summarizeToolCall`,
// and the SERVER path is what the dashboard transcript shows — so a drift here is
// visible to users, not just inconsistent. That copy used to claim in its docstring
// that it mirrored this one while actually emitting raw `name(k='v', …)` Python call
// syntax, which is what made a tool call read as a debug dump.
//
// Number formatting is where the two languages can still disagree. Booleans and
// integral floats are normalised explicitly below (`String(true)` vs `str(True)`,
// `'2'` vs `'2.0'`); a NON-integral number falls through to each language's own
// repr, so the two agree on strings, booleans and integers — which is what tool
// args overwhelmingly are — and may differ in the last digits of an odd float.
// The mirror tests pin the former and do not claim the latter.

const MAX_ARG_CHARS = 40;
const MAX_ARGS = 2;

// A uuid is not human-readable, and the model passes plenty of them around:
// `create_tile` returns node ids and every later call quotes them, so
// `tile_id=55232d03-60bb-4881-…` is pure noise in a transcript.
function isOpaqueKey(key) {
  return key === 'id' || key.endsWith('_id');
}

function renderArgValue(value) {
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  const text = String(value);
  return text.length > MAX_ARG_CHARS ? `${text.slice(0, MAX_ARG_CHARS - 1)}…` : text;
}

// The fallback for every tool without a hand-written phrase — which is most of
// them: there are 115 action tools across the tile types, and this used to render
// them as a bare `• set_mesh_edge_length`, dropping every argument. Sentence-casing
// the name and keeping the first couple of readable args says what actually
// happened without needing a phrase per tool.
function genericSummary(tc) {
  const name = String(tc.name || 'action');
  const sentence = name.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
  const args = Object.entries(tc.input || {})
    .filter(
      ([key, value]) =>
        !isOpaqueKey(key) &&
        (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') &&
        value !== ''
    )
    .slice(0, MAX_ARGS)
    .map(([key, value]) => `${key}=${renderArgValue(value)}`);
  return args.length ? `• ${sentence} (${args.join(', ')})` : `• ${sentence}`;
}

/**
 * Human-readable one-liner per tool call, shown under the assistant bubble.
 * Exported so the summary wording is testable and shared.
 */
export function summarizeToolCall(tc) {
  if (tc.name === 'create_tile') return `• Created "${tc.input.label}" (${tc.input.type})`;
  // Deliberately NOT "Connected CAD Input → Mesh": this function is pure over ONE
  // call and has no graph to resolve `source_id`/`target_id` against, and it has to
  // stay byte-identical to the backend copy — so resolving labels would mean
  // threading a graph through both sides for the sake of one tool.
  if (tc.name === 'connect_tiles') return `• Connected modules`;
  if (tc.name === 'delete_tile') return `• Deleted module`;
  if (tc.name === 'rename_tile') return `• Renamed to "${tc.input.new_label}"`;
  if (tc.name === 'delete_edge') return `• Removed connection`;
  if (tc.name === 'clear_canvas') return `• Cleared canvas`;
  // `set_plan` is the orchestrator's opening move and a hard precondition of
  // `create_tile`, so it is the first line of most transcripts. Its raw args are a
  // pipeline key plus a sentence of reasoning plus the whole step list, which is
  // what produced the truncated wall of JSON in the reported screenshot.
  if (tc.name === 'set_plan' && tc.input?.pipeline) {
    const steps = Array.isArray(tc.input.steps) ? tc.input.steps.length : 0;
    return `• Planned the ${tc.input.pipeline} pipeline (${steps} ${
      steps === 1 ? 'step' : 'steps'
    })`;
  }
  if (tc.name === 'activate_tile') return `• Started a module`;
  if (tc.name === 'complete_tile') return `• Finished the module`;
  if (tc.name === 'request_user_input') return `• Asked you a question`;
  if (tc.name === 'ask_user') {
    const qs = Array.isArray(tc.input?.questions) ? tc.input.questions.length : 0;
    return `• Asked you ${qs} ${qs === 1 ? 'question' : 'questions'}`;
  }
  return genericSummary(tc);
}

/**
 * Turn one executor result into the tool_result content string the model sees.
 *
 * `create_tile` is the one legacy shape: it returns a bare node-id string, which we
 * wrap AND record in `idMap` so the model can keep referring to the tile by label.
 * Everything else passes its descriptive string through verbatim; null/undefined falls
 * back to a generic confirmation.
 */
export function toolResultContent(tc, result, idMap) {
  if (tc.name === 'create_tile' && typeof result === 'string') {
    idMap[tc.input.label] = result;
    return `Created tile with ID: ${result}`;
  }
  if (typeof result === 'string' && result.length > 0) return result;
  return 'Action completed successfully';
}

/**
 * Run the agent loop to completion.
 *
 * @param {object}   o
 * @param {Array}    o.history      Anthropic-shaped messages to start from.
 * @param {Function} o.stream       (history) => async iterable of SSE events.
 * @param {Function} o.executeTool  (name, input, idMap) => Promise<string|null>.
 * @param {Function} [o.onText]     (fullTextSoFar) => void, for live streaming.
 * @param {Function} [o.onToolCalls](toolCalls, roundIndex) => void, after each round.
 * @param {object}   [o.idMap]      Label -> node id, shared across rounds.
 * @param {number}   [o.maxRounds]
 * @param {Function} [o.beforeToolCall] (toolCall) => 'allow'|'refuse'|'ask'|
 *   Promise<...>. The PERMISSION GATE. Consulted once per call, immediately
 *   before `executeTool`, which is the one choke point every browser-executed
 *   tool passes through — so this single hook covers all three callers (the
 *   visible ChatPanel and both of ProjectPage's background turns).
 *
 *   Its verdict decides what the MODEL is told, and the tool either runs or does
 *   not. `'ask'` is resolved by the caller (it owns the UI), so a promise here may
 *   stay pending for as long as the user takes to decide; it must resolve to
 *   `'allow'` or `'refuse'`.
 * @param {Function} [o.gateMessage] (toolCall, verdict) => string. The
 *   `tool_result` text for a call that did NOT run. A string, never a thrown
 *   error: the loop already treats a string as the result the model reads, and an
 *   error would end the turn instead of letting the model adapt.
 * @returns {Promise<{content, toolSummary, error, rounds}>}
 */
export async function runAgentRounds({
  history,
  stream,
  executeTool,
  onText,
  onToolCalls,
  idMap = {},
  maxRounds = MAX_ROUNDS,
  beforeToolCall,
  gateMessage,
}) {
  let messages = history;
  let content = '';
  const summaries = [];
  let rounds = 0;

  while (rounds < maxRounds) {
    rounds += 1;

    let roundText = '';
    const toolCalls = [];
    let pendingToolCalls = null;
    // Set once a call in THIS round is refused or paused; the rest of the round's
    // calls are then reported un-run rather than executed. Per-round, not
    // per-turn: the model gets the results and decides again with them in view.
    let blocked = false;

    for await (const event of stream(messages)) {
      if (event.type === 'text') {
        roundText += event.content;
        onText?.([content, roundText].filter(Boolean).join('\n\n'));
      } else if (event.type === 'tool_call') {
        toolCalls.push(event);
      } else if (event.type === 'awaiting_tool_results') {
        pendingToolCalls = event.tool_calls;
      } else if (event.type === 'error') {
        return {
          content: event.content || 'An error occurred',
          toolSummary: summaries.join('\n') || null,
          error: true,
          rounds,
        };
      }
      // 'done' just ends this round's stream
    }

    content = [content, roundText].filter(Boolean).join('\n\n');

    // Execute this round's calls. Sequential on purpose: the tools mutate the canvas
    // and several depend on the previous one's effect (connect_tiles needs the ids
    // create_tile just returned).
    const toolResults = [];
    for (const tc of toolCalls) {
      // The permission gate. Runs BEFORE the tool, so a refused call has no
      // effect at all — the whole point, since these tools start solves and
      // delete modules rather than editing text.
      //
      // A gated call still gets a `tool_result`: the Anthropic API rejects a
      // round whose tool_use blocks are not all answered, so skipping one would
      // make the NEXT round unconstructable. `blocked` also stops the remaining
      // calls of this round from running, because the model chose them on the
      // assumption that the earlier ones had succeeded.
      const verdict = beforeToolCall && !blocked ? await beforeToolCall(tc) : 'allow';
      if (verdict !== 'allow') {
        blocked = true;
        toolResults.push({
          type: 'tool_result',
          tool_use_id: tc.tool_id || tc.id,
          content: gateMessage ? gateMessage(tc, verdict) : `\`${tc.name}\` was not run.`,
        });
        continue;
      }
      if (blocked) {
        // A later call in a round whose earlier call was refused. Not executed,
        // and told so plainly — the model reads this and re-decides next round
        // with the refusal in view.
        toolResults.push({
          type: 'tool_result',
          tool_use_id: tc.tool_id || tc.id,
          content:
            `\`${tc.name}\` was not run, because an earlier action in this same ` +
            `step was not carried out. Take that into account and continue.`,
        });
        continue;
      }
      const result = await executeTool(tc.name, tc.input, idMap);
      toolResults.push({
        type: 'tool_result',
        tool_use_id: tc.tool_id || tc.id,
        content: toolResultContent(tc, result, idMap),
      });
    }
    if (toolCalls.length) {
      summaries.push(toolCalls.map(summarizeToolCall).join('\n'));
      onToolCalls?.(toolCalls, rounds);
    }

    // No tool calls, or the backend didn't ask for results: the turn is over. This is
    // the loop's terminator — a text-only reply must not spin.
    if (!pendingToolCalls?.length || !toolResults.length) {
      return {
        content,
        toolSummary: summaries.join('\n') || null,
        rounds,
      };
    }

    // Feed the results back as the next round's input. The assistant turn must repeat
    // the tool_use blocks verbatim or the API rejects the tool_result that answers them.
    const assistantBlocks = [];
    if (roundText) assistantBlocks.push({ type: 'text', text: roundText });
    for (const tc of pendingToolCalls) {
      assistantBlocks.push({ type: 'tool_use', id: tc.id, name: tc.name, input: tc.input });
    }
    messages = [
      ...messages,
      { role: 'assistant', content: assistantBlocks },
      { role: 'user', content: toolResults },
    ];
  }

  return {
    content: content || '(max rounds reached)',
    toolSummary: summaries.join('\n') || null,
    rounds,
    cappedOut: true,
  };
}
